/**
 * The Firmio desktop app (desktop/, Electron) loads this same web app from the
 * instance and exposes a small bridge on `window.firmioDesktop` (its preload,
 * desktop/src/preload/index.ts). This file is the bridge's contract — the
 * desktop app imports these types from here — and the web side's only way to
 * know it runs inside it.
 *
 * In a normal browser `window.firmioDesktop` is undefined and every helper
 * here is a no-op, so callers need no guard beyond `isDesktop()`.
 *
 * No imports, React included: the desktop app type-checks this file on its
 * own, without the web app's dependencies (desktop-ci.yml). The React view of
 * the window state is hooks/useDesktopWindowState.ts.
 */

/** A notification for the desktop app to show as a system notification. */
export interface DesktopNotification {
  /** Feed event id (GET /api/notifications/feed). */
  id: number;
  title: string;
  body: string;
  /** In-app path to open on click, e.g. /team-chat/<id>. */
  url: string;
  /** Collapse key: a new one replaces the previous with the same tag. */
  tag: string;
  /** True when the page already played the Firmio sound for it. */
  silent: boolean;
}

/** Whether the desktop window is focused and shown (not hidden in the tray or minimised). */
export interface DesktopWindowState {
  focused: boolean;
  visible: boolean;
}

/** The organization the desktop app is connected to. */
export interface DesktopServer {
  code: string | null;
  name: string;
  baseUrl: string;
}

export interface FirmioDesktopApi {
  /** The desktop app's own version (not the instance's). */
  version: string;
  platform: 'win32' | 'darwin' | 'linux';
  /**
   * False in a second window (an invoice opened with window.open): only the
   * main window shows notifications and sets the badge.
   */
  isMainWindow(): Promise<boolean>;
  notify(notification: DesktopNotification): void;
  /** Unread count on the dock icon (macOS) or taskbar button (Windows); 0 clears it. */
  setBadge(count: number): void;
  /** A notification (or a firmio://open link) asks to open an in-app path. */
  onNavigate(callback: (path: string) => void): () => void;
  getWindowState(): Promise<DesktopWindowState>;
  onWindowState(callback: (state: DesktopWindowState) => void): () => void;
  /** Open an http(s) URL in the system browser. */
  openExternal(url: string): void;
  getServer(): Promise<DesktopServer | null>;
  /** Leave this organization and show the organization code screen. */
  changeOrganization(): void;
  /** The "Notificări" switch in the tray menu. */
  notificationsEnabled(): Promise<boolean>;
  onNotificationsEnabledChange(callback: (enabled: boolean) => void): () => void;
}

declare global {
  interface Window {
    firmioDesktop?: FirmioDesktopApi;
  }
}

/** The bridge, or null in a browser. */
export function desktop(): FirmioDesktopApi | null {
  return typeof window !== 'undefined' ? (window.firmioDesktop ?? null) : null;
}

/** True inside the Firmio desktop app. */
export function isDesktop(): boolean {
  return desktop() !== null;
}

// --- Window state ------------------------------------------------------------
// The desktop window keeps running when hidden in the tray (Electron's
// backgroundThrottling is off), so the page's own visibility always reads
// "visible". The real state comes from the desktop app.

let windowState: DesktopWindowState = { focused: true, visible: true };
const listeners = new Set<() => void>();
let wired = false;

function wire(): void {
  const api = desktop();
  if (wired || !api) return;
  wired = true;
  const update = (next: DesktopWindowState) => {
    windowState = next;
    for (const listener of listeners) listener();
  };
  void api.getWindowState().then(update);
  api.onWindowState(update);
}

/** Be told when the desktop window state changes; returns the unsubscribe. */
export function subscribeDesktopWindowState(listener: () => void): () => void {
  wire();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The latest desktop window state (focused + shown); in a browser, always both. */
export function currentDesktopWindowState(): DesktopWindowState {
  wire();
  return windowState;
}
